import { useAuth } from "auth/Auth";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Grid,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { Edit, Delete } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import { TravelDetailSection, TravelDetailFieldGrid, buildTravelAuditFields, formatTravelDate } from "components/travel/TravelDetailSection";
import { commonIconButtonStyles, commonIconStyles } from "assets/theme/components/iconButtonStyles";
import HighSeasonSurchargeModal from "layouts/Travel/hss/HighSeasonSurchargeModal";

const HighSeasonSurchargeDetail = () => {
  const { id } = useParams();
  const { axiosInstance } = useAuth();
  const location = useLocation();
  const nameFromState = location.state?.name;
  const navigate = useNavigate();

  // Initializing states

  const [trips, setTrips] = useState([]);
  const [template, setTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tripsLoaded, setTripsLoaded] = useState(false);
  const [isTripsLoading, setIsTripsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);

  // Fetch template data when page renders for the first time

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  // Trigger GET request when the page renders for the first time.

  const fetchTemplate = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/hss/${id}`);
      setTemplate(response.data);
    } catch (error) {
      console.error("Error fetching template", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Retrieve associated trip data once after page renders and user switch to trip tab

  useEffect(() => {
    if (activeTab === 1 && !tripsLoaded) {
      fetchTrips();
      setTripsLoaded(true);
    }
  }, [activeTab, tripsLoaded]);

  // GET request to retrieve associated trip data

  const fetchTrips = async () => {
    setIsTripsLoading(true);
    try {
      const response = await axiosInstance.get(`/trips/?hss_id=${id}`);
      setTrips(response.data);
    } catch (error) {
      console.error("Error fetching trips:", error);
    } finally {
      setIsTripsLoading(false);
    }
  };

  // Handle save operation when existing template is edited.

  const handleSaveTemplate = async (formData) => {
    try {
      setIsSaving(true);
      await axiosInstance.put(`/hss/${id}`, formData);
      setSnackbar({ open: true, message: "HSS Template updated successfully", severity: "success" });
      setIsModalOpen(false);
      fetchTemplate();
    } catch (error) {
      console.error("Error updating template:", error);
      setSnackbar({ open: true, message: "Error updating template", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete operation when selected template is deleted

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/hss/${id}`);
      setSnackbar({ open: true, message: "HSS Template deleted successfully", severity: "success" });
      setTimeout(() => navigate("/hss"), 1000);
    } catch (error) {
      console.error("Error deleting template:", error);
      setSnackbar({ open: true, message: "Error deleting template", severity: "error" });
      throw error;
    }
  };

  // Create associated trip table data

  const tripsTableData = useMemo(
    () => ({
      columns: [
        {
          Header: "Trip Name",
          accessor: "title",
          width: "35%",
          Cell: ({ row }) => (
            <MDTypography
              component="span"
              onClick={() => navigate(`/trips/${row.original.id}/edit`)}
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{ cursor: "pointer" }}
            >
              {row.original.title || row.original.name || "-"}
            </MDTypography>
          ),
        },
        {
          Header: "Country",
          accessor: "primary_country",
          width: "20%",
          Cell: ({ value }) => (value ? value.toUpperCase() : "-"),
        },
        {
          Header: "Created",
          accessor: "created_at",
          width: "20%",
          align: "right",
          Cell: ({ value }) => (
            <MonoText align="right">{value ? new Date(value).toLocaleDateString() : "-"}</MonoText>
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          width: "15%",
          Cell: ({ value }) => <StatusBadge status={value || "draft"} />,
        },
      ],
      rows: trips,
    }),
    [trips, navigate]
  );

  // Preparing detailed view data

  const overviewFields = [
    { label: "Template Name", field: "template_name" },
    {
      label: "HSS %",
      field: "hss_percentage",
      renderValue: (val) => (val ? `${val}%` : "-"),
    },
    {
      label: "Currency",
      field: "currency",
      renderValue: (val) => (val ? val.toUpperCase() : "-"),
    },
    {
      label: "Valid From",
      field: "valid_from",
      renderValue: formatTravelDate,
    },
    {
      label: "Valid Until",
      field: "valid_until",
      renderValue: formatTravelDate,
    },
    {
      label: "Status",
      field: "status",
      custom: (data) => <StatusBadge status={data?.status} />,
    },
  ];

  const auditFields = useMemo(
    () => buildTravelAuditFields((userId) => navigate(`/users/${userId}/profile`)),
    [navigate]
  );

  // If page is loading, show skeleton

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
        </MDBox>
      </DashboardLayout>
    );
  }

  // Render the page

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h5" color="white">
                    {template?.template_name || nameFromState || "High Season Surcharge"}
                  </MDTypography>
                  <MDTypography variant="button" color="white" opacity={0.8}>
                    High season surcharge template details
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  <Tooltip title="Edit" arrow>
                    <IconButton onClick={() => {
                      setIsModalOpen(true);
                    }} sx={commonIconButtonStyles}>
                      <Edit sx={commonIconStyles} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton onClick={() => setDeleteConfirmDialog(true)} sx={commonIconButtonStyles}>
                      <Delete sx={commonIconStyles} />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
                >
                  <Tab label="Overview" />
                  <Tab label="Trips" />
                </Tabs>

                {activeTab === 0 && (
                  <>
                    <TravelDetailSection title="Template Information">
                      <TravelDetailFieldGrid data={template} fields={overviewFields} />
                    </TravelDetailSection>
                    <TravelDetailSection title="Audit Information">
                      <TravelDetailFieldGrid data={template} fields={auditFields} />
                    </TravelDetailSection>
                  </>
                )}

                {activeTab === 1 && (
                  <TravelDetailSection title="Associated Trips">
                    {isTripsLoading ? (
                      <Skeleton variant="rectangular" height={240} />
                    ) : (
                      <DataTable
                        table={tripsTableData}
                        isSorted
                        noEndBorder
                        canSearch
                        exportCSV
                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                        showTotalEntries
                        pagination={{ variant: "gradient", color: "info" }}
                      />
                    )}
                  </TravelDetailSection>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <HighSeasonSurchargeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTemplate}
        template={template}
        isSaving={isSaving}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDeleteModal
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
        onConfirm={handleDelete}
        title="Delete HSS Template"
        message="Are you sure you want to delete this HSS template? This action cannot be undone."
      />
    </DashboardLayout>
  );
};

export default HighSeasonSurchargeDetail;
